import { useState } from 'react'
import { Link } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { notasFiscaisApi, veiculosApi } from '../../api/endpoints'
import { Button } from '../../components/ui/Button'
import { Input, Select } from '../../components/ui/Input'
import { useToast } from '../../components/ui/Toast'
import { StatusVeiculo, TIPO_COMBUSTIVEL_LABEL, UNIDADE_ENERGIA_LABEL } from '../../lib/enums'
import { formatDate, formatMoney } from '../../lib/utils'
import { useRecurso } from '../plano/useRecurso'
import type { SalvarAbastecimentoResultado } from '../../types'
import { SeletorAncora } from './AbastecimentoFormModal'
import { PagamentoVeiculoForm } from './PagamentoVeiculoForm'
import { montarPagamento, pagamentoValido, pagamentoVazio, type PagamentoForm } from './pagamentoUtils'
import { formatKm, invalidarVeiculo, numeroBr, previaAbastecimento } from './veiculoUtils'

/** Cartão "Isto parece um abastecimento" no detalhe da nota do posto. */
export function AbastecimentoDaNota({ notaId, somenteLeitura }: { notaId: string; somenteLeitura: boolean }) {
  const qc = useQueryClient()
  const toast = useToast()
  const modulo = useRecurso('veiculos.modulo').habilitado
  const recurso = useRecurso('veiculos.nota_fiscal').habilitado
  const liberado = modulo && recurso && !somenteLeitura

  const { data: s } = useQuery({
    queryKey: ['nota-abastecimento', notaId], queryFn: () => notasFiscaisApi.abastecimentoSugerido(notaId), enabled: liberado,
  })
  const { data: veiculos = [] } = useQuery({ queryKey: ['veiculos'], queryFn: veiculosApi.list, enabled: liberado && !!s?.pareceAbastecimento })

  const meus = veiculos.filter((v) => v.status === StatusVeiculo.Ativo && v.podeEditar)
  const [veiculoSel, setVeiculoSel] = useState('')
  const veiculoId = veiculoSel || (meus.length === 1 ? meus[0].id : '')
  const veiculo = meus.find((v) => v.id === veiculoId)
  const [km, setKm] = useState('')
  const [cheio, setCheio] = useState(true)
  const [reserva, setReserva] = useState(false)
  const [pagamento, setPagamento] = useState<PagamentoForm>(pagamentoVazio())
  const [alerta, setAlerta] = useState<SalvarAbastecimentoResultado | null>(null)

  const registrar = useMutation({
    mutationFn: (confirmar: boolean) => notasFiscaisApi.registrarAbastecimento(notaId, {
      veiculoId, odometro: Number(km), tanqueCheio: cheio, estavaNaReserva: reserva,
      confirmarRegressao: confirmar || undefined,
      pagamento: s?.lancamentoVinculado ? null : montarPagamento(pagamento),
    }),
    onSuccess: (r) => {
      if (!r.gravado) { setAlerta(r); return }
      setAlerta(null)
      invalidarVeiculo(qc)
      for (const k of ['nota-abastecimento', 'notas-fiscais', 'nota-candidatos']) qc.invalidateQueries({ queryKey: [k] })
      toast.success('Abastecimento registrado.')
      if (r.aviso) toast.success(`Atenção: ${r.aviso}`)
    },
    onError: (e: Error) => toast.error(e.message),
  })

  if (!liberado || !s || !s.pareceAbastecimento) return null

  if (s.abastecimentoId) {
    return (
      <div className="rounded bg-income/10 px-3 py-2.5 text-13 text-income">
        <i className="ri-gas-station-line" aria-hidden /> Esta nota já virou um abastecimento.{' '}
<Link to="/veiculos" className="underline underline-offset-2">Ver veículos</Link>
      </div>
    )
  }

  const previa = previaAbastecimento(cheio, reserva)
  const semLancamento = !s.lancamentoVinculado
  const valido = !!veiculo && km.trim() !== '' && Number(km) >= 0 && !!s.tipoCombustivel && (!semLancamento || pagamentoValido(pagamento))

  return (
    <div className="space-y-3 rounded border border-primary/30 bg-primary/5 p-3">
      <p className="text-13 font-medium text-white"><i className="ri-gas-station-line text-primary" aria-hidden /> Isto parece um abastecimento</p>
      <p className="text-13 text-muted">
        {s.quantidade != null && <>{numeroBr(s.quantidade, 2)} {s.unidade ? UNIDADE_ENERGIA_LABEL[s.unidade] : ''} de {s.tipoCombustivel ? TIPO_COMBUSTIVEL_LABEL[s.tipoCombustivel] : 'combustível'} · </>}
        {s.valorTotal != null && <>{formatMoney(s.valorTotal)} · </>}
        {s.postoApelido || s.postoNome || 'posto'}{s.data ? ` · ${formatDate(s.data)}` : ''}.
        {' '}Litros, valor, combustível e posto já vêm da nota. Falta só o veículo e o km.
      </p>
      {!s.tipoCombustivel && <p className="text-13 text-warning">{s.motivo || 'Não consegui identificar o combustível desta nota.'}</p>}
      {(s.valorOutrosItens ?? 0) > 0 && (
        <dl className="space-y-0.5 rounded bg-surface2 px-3 py-2 text-13">
          <div className="flex justify-between"><dt className="text-muted">Combustível</dt><dd className="text-white">{formatMoney(s.valorTotal ?? 0)}</dd></div>
          <div className="flex justify-between"><dt className="text-muted">Outros itens</dt><dd className="text-white">{formatMoney(s.valorOutrosItens ?? 0)}</dd></div>
          <div className="flex justify-between border-t border-white/10 pt-0.5"><dt className="text-muted">Total da nota</dt><dd className="font-medium text-white">{formatMoney(s.valorNota ?? (s.valorTotal ?? 0) + (s.valorOutrosItens ?? 0))}</dd></div>
        </dl>
      )}

      <div className="grid gap-3 sm:grid-cols-2">
        <Select label="Veículo" value={veiculoId} onChange={(e) => setVeiculoSel(e.target.value)}>
          <option value="">Selecione...</option>
          {meus.map((v) => <option key={v.id} value={v.id}>{v.apelido}</option>)}
        </Select>
        <Input label={`Km no painel *${veiculo ? ` (último: ${formatKm(veiculo.kmAtual)})` : ''}`} type="number" inputMode="numeric" min={0} value={km} onChange={(e) => setKm(e.target.value)} />
      </div>
      {meus.length === 0 && veiculos.length >= 0 && <p className="text-xs text-muted">Cadastre um veículo ativo em <Link to="/veiculos" className="text-primary underline underline-offset-2">Veículos</Link> para registrar.</p>}

      <div>
        <span className="form-label">Como foi esse abastecimento?</span>
        <SeletorAncora cheio={cheio} reserva={reserva} onChange={(c, r) => { setCheio(c); setReserva(r) }} rotuloCheio="Enchi o tanque" rotuloReserva="Estava na reserva" />
        <p className="mt-2 text-xs text-muted"><span className="font-medium text-white">{previa.titulo}.</span> {previa.texto}</p>
      </div>

      {s.lancamentoVinculado ? (
        <p className="rounded bg-surface2 px-3 py-2.5 text-13 text-muted">
          Esta nota já está ligada ao lançamento <span className="text-white">&quot;{s.lancamentoVinculado.descricao}&quot;</span> de {formatMoney(s.lancamentoVinculado.valor)}.
          O abastecimento adota esse lançamento: nada é lançado duas vezes.
        </p>
      ) : (
        <div className="space-y-2">
          <p className="text-xs text-muted">A nota ainda não tem lançamento: ele é criado junto com o abastecimento.</p>
          <PagamentoVeiculoForm valor={pagamento} onChange={setPagamento} />
        </div>
      )}

      {alerta && (
        <div className="rounded bg-warning/10 px-3 py-2.5 text-13 text-warning" role="alert">
          <i className="ri-error-warning-line" aria-hidden /> {alerta.mensagem || 'Esse km parece diferente do esperado.'}
          <p className="mt-1 text-xs">Nada foi salvo ainda. Confirme só se o valor estiver certo.</p>
        </div>
      )}
      {s.aviso && (
        <div className="rounded border border-warning/40 bg-warning/10 px-3 py-2.5 text-13 text-warning" role="note">
          <i className="ri-information-line" aria-hidden /> {s.aviso}
        </div>
      )}
      <div className="flex justify-end gap-2">
        {alerta
          ? <><Button size="sm" variant="light" onClick={() => setAlerta(null)}>Corrigir o km</Button><Button size="sm" loading={registrar.isPending} onClick={() => registrar.mutate(true)}>Gravar mesmo assim</Button></>
          : <Button icon="ri-gas-station-line" disabled={!valido} loading={registrar.isPending} onClick={() => registrar.mutate(false)}>Registrar abastecimento</Button>}
      </div>
    </div>
  )
}
